import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Card, CardHeader } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { IBusiness } from "@/business/business";
import { GOOGLE_BUSINESS_CONNECTION_STATE } from "@/business/constants";
import { useGetGoogleBusinessAccountLocations } from "@/business/google-business-query";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import { DeleteGoogleBusinessLocationModal } from "./delete-google-business-location-modal";
import { GoogleBusinessConnectionModal } from "./google-business-connection-modal";
import GoogleLogo from "@/assets/images/google-logo.svg";

interface GoogleBusinessConnectionProps {
  business: IBusiness;
}

const CONNECTION_STATE_TO_TEXT = {
  [GOOGLE_BUSINESS_CONNECTION_STATE.not_started]:
    "Connect your Google Business account so we can analyze your reviews and provide more insights.",
  [GOOGLE_BUSINESS_CONNECTION_STATE.connected]:
    "Your Google Business account is connected. Select the locations associated with this business to complete setup.",
};

export const GoogleBusinessConnection = ({
  business,
}: GoogleBusinessConnectionProps) => {
  const { data: user } = useGetMyUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteLocationId, setDeleteLocationId] = useState<string | null>(null);

  const { data: accountLocations = [], isLoading: locationsLoading } =
    useGetGoogleBusinessAccountLocations(business.id);

  const connectionState = business.google_business_connection_state;
  const isConnected =
    connectionState === GOOGLE_BUSINESS_CONNECTION_STATE.connected;

  const hasLocations = !locationsLoading && accountLocations.length > 0;
  const canConnectAccount = isAdminOrOwner(user?.role);

  const connectionText =
    CONNECTION_STATE_TO_TEXT[
      connectionState as keyof typeof CONNECTION_STATE_TO_TEXT
    ] || CONNECTION_STATE_TO_TEXT[GOOGLE_BUSINESS_CONNECTION_STATE.not_started];

  const handleGoogleBusinessLink = async () => {
    if (!isConnected) {
      if (!business.id || !user?.email) {
        toast.error("Missing required information", {
          description: "Business ID and user email are required",
        });
        return;
      }

      setIsLoading(true);

      try {
        const apiBase = import.meta.env.VITE_API_URL;
        if (!apiBase) {
          toast.error("API URL not configured");
          setIsLoading(false);
          return;
        }
        const params = new URLSearchParams({
          state: JSON.stringify({
            business_id: business.id,
            user_email: user.email,
          }),
        });
        window.location.href = `${apiBase}/auth/google_oauth2?${params.toString()}`;
      } catch (error) {
        console.error("Google OAuth initiation error:", error);
        toast.error("Failed to initiate Google Business connection", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
        setIsLoading(false);
      }
    }
  };

  const handleAddLocation = () => {
    setIsModalOpen(true);
  };

  const handleDeleteLocation = (locationId: string) => {
    setDeleteLocationId(locationId);
  };

  const handleCloseDeleteModal = () => {
    setDeleteLocationId(null);
  };

  if (!user) {
    return null;
  }

  return (
    <Stack gap="lg" className="pb-6">
      <Typography variant="subtitleW2">Google Business integration</Typography>

      {!isConnected && (
        <Typography variant="bodyW2">{connectionText}</Typography>
      )}

      {isConnected && !hasLocations && (
        <Typography variant="bodyW2">{connectionText}</Typography>
      )}

      {canConnectAccount && !isConnected && (
        <Button
          onClick={handleGoogleBusinessLink}
          disabled={!business.id || !user?.email}
          variant="tertiary"
          className="w-fit"
          type="button"
          loading={isLoading}
          leftIcon={<img src={GoogleLogo} alt="Google" className="h-5 w-5" />}
        >
          Connect Google Business
        </Button>
      )}

      {isConnected && (
        <Stack gap="md">
          {!locationsLoading &&
            accountLocations.map((location) => (
              <Card key={location.id}>
                <CardHeader>
                  <Stack
                    direction="row"
                    gap="md"
                    align="center"
                    justify="between"
                    fullWidth
                  >
                    <Stack direction="row" gap="md" align="center">
                      <img src={GoogleLogo} alt="Google" className="h-5 w-5" />
                      <Stack gap="sm">
                        <Typography variant="subtitleW3">
                          {location.title || "Location"}
                        </Typography>
                        <Typography variant="bodyW2" textColor="tertiary">
                          {location.location}
                        </Typography>
                        <Typography variant="bodyW2" textColor="tertiary">
                          Account: {location.account}
                        </Typography>
                      </Stack>
                    </Stack>
                    {canConnectAccount && (
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => handleDeleteLocation(location.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </Stack>
                </CardHeader>
              </Card>
            ))}

          {canConnectAccount && (
            <Button
              onClick={handleAddLocation}
              variant="tertiary"
              className="w-fit"
              type="button"
            >
              <Plus className="h-4 w-4" />
              {hasLocations ? "Add Another Location" : "Add Location"}
            </Button>
          )}
        </Stack>
      )}

      {!canConnectAccount && (
        <Typography variant="bodyW2" textColor="tertiary">
          Only owners and admins can manage Google Business connections.
        </Typography>
      )}

      {isModalOpen && (
        <GoogleBusinessConnectionModal
          business={business}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      <DeleteGoogleBusinessLocationModal
        businessId={business.id}
        locationId={deleteLocationId}
        onClose={handleCloseDeleteModal}
      />
    </Stack>
  );
};
